import {
  omsActionStepSchema,
  omsConditionStepSchema,
  recognizeEmailStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { recognizeConditionFields } from "@redotech/redo-flows-common/schemas/recognize";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { ConditionDataSource } from "@redotech/redo-flows-common/steps";
import { RecognizeTriggerKey } from "@redotech/redo-flows-common/triggers";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import * as z from "zod";

export const recognizeFollowerCountPlatforms = [
  { field: "instagramFollowerCount", label: "Instagram" },
  { field: "tiktokFollowerCount", label: "TikTok" },
  { field: "youtubeFollowerCount", label: "YouTube" },
  { field: "xFollowerCount", label: "X" },
  { field: "linkedinFollowerCount", label: "LinkedIn" },
] as const;

export const recognizeFollowerCountComparisons = [
  { operator: "greaterThanOrEqual", label: "≥" },
  { operator: "lessThanOrEqual", label: "≤" },
  { operator: "greaterThan", label: ">" },
  { operator: "lessThan", label: "<" },
  { operator: "equalTo", label: "=" },
] as const;

const recognizeConditionField = z.enum(recognizeConditionFields);

const baseStep = z.object({
  id: z.string().min(1),
  customTitle: z.string().optional(),
});
const nextId = z.string().min(1);

const recognizeProfileConditionStep = baseStep.extend({
  type: z.literal(StepType.CONDITION),
  expression: z.object({
    dataSource: z.literal(ConditionDataSource.TriggerData),
    schemaBooleanExpression: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("boolean_evaluation"),
        field: recognizeConditionField,
        value: z.boolean().optional(),
      }),
      z.object({
        type: z.literal("number_comparison"),
        field: z.enum(
          recognizeFollowerCountPlatforms.map(({ field }) => field),
        ),
        operator: z.enum(
          recognizeFollowerCountComparisons.map(({ operator }) => operator),
        ),
        comparisonValue: z.number(),
      }),
    ]),
  }),
  nextTrueId: nextId,
  nextFalseId: nextId,
});

export const recognizeConditionStep = z.union([
  omsConditionStepSchema,
  recognizeProfileConditionStep,
]);

export type RecognizeConditionStep = z.infer<typeof recognizeConditionStep>;

const recognizeFlowStep = z.union([
  recognizeEmailStepSchema,
  omsActionStepSchema,
  recognizeConditionStep,
  z.discriminatedUnion("type", [
    baseStep.extend({
      type: z.literal(StepType.TRIGGER),
      category: z.literal("Recognize"),
      schemaType: z.literal(SchemaType.RECOGNIZE),
      key: z.literal(RecognizeTriggerKey.COMPLETED),
      nextId,
    }),
    baseStep.extend({ type: z.literal(StepType.DO_NOTHING) }),
  ]),
]);

export type RecognizeFlowStep = z.infer<typeof recognizeFlowStep>;

export const recognizeFlow = z
  .array(recognizeFlowStep)
  .min(2)
  .max(100)
  .superRefine((steps, context) => {
    const byId = new Map(steps.map((step) => [step.id, step]));
    const triggers = steps.filter((step) => step.type === StepType.TRIGGER);

    if (byId.size !== steps.length || triggers.length !== 1) {
      context.addIssue({
        code: "custom",
        message: "Use unique step IDs and exactly one trigger.",
      });
      return;
    }

    const visiting = new Set<string>();
    const visited = new Set<string>();

    function visit(id: string): void {
      const step = byId.get(id);

      if (!step || visiting.has(id)) {
        context.addIssue({
          code: "custom",
          message: "Every path must end without missing steps or cycles.",
        });
        return;
      }

      if (visited.has(id)) {
        return;
      }

      visiting.add(id);

      if (
        step.type === StepType.CONDITION ||
        step.type === StepType.OMS_CONDITION
      ) {
        visit(step.nextTrueId);
        visit(step.nextFalseId);
      } else if (step.type !== StepType.DO_NOTHING) {
        visit(step.nextId);
      }

      visiting.delete(id);
      visited.add(id);
    }

    const trigger = triggers[0];

    if (trigger) {
      visit(trigger.id);
    }

    if (visited.size !== steps.length) {
      context.addIssue({
        code: "custom",
        message: "Connect every step to the trigger.",
      });
    }
  });

export const recognizeFlowVersion = z.object({
  id: z.string(),
  version: z.number().int(),
  createdAt: z.string(),
  steps: recognizeFlow,
});

export type RecognizeFlowVersion = z.infer<typeof recognizeFlowVersion>;

export const defaultRecognizeFlow: RecognizeFlowStep[] = [
  {
    id: "recognize-completed",
    type: StepType.TRIGGER,
    category: "Recognize",
    schemaType: SchemaType.RECOGNIZE,
    key: RecognizeTriggerKey.COMPLETED,
    nextId: "end",
  },
  { id: "end", type: StepType.DO_NOTHING },
];
